import { arrayOf } from 'prop-types';
import React from 'react';
import { propTypes } from '../../../util/types';
import { H2, H3 } from '../../../components';
import { FormattedMessage, useIntl } from 'react-intl';
import SectionTextMaybe from '../SectionTextMaybe';

import css from './SectionEngines.module.css';

const getEngineDisplayName = (engine, index) => {
  const parts = [engine.engineManufacturer];
  if (engine.engineModel) parts.push(engine.engineModel);
  if (parts.length === 1) parts.push(`#${index + 1}`);
  return parts.join(' ');
};

const getEngineSpecs = (engine, intl) => {
  const specs = [];

  if (engine.enginePower)
    specs.push(`${engine.enginePower} ${intl.formatMessage({ id: 'General.horsePower' })}`);
  if (engine.fuelType)
    specs.push(
      intl.formatMessage({ id: `General.listing.fuelType.${engine.fuelType}` }).toLowerCase()
    );
  if (engine.engineType)
    specs.push(
      intl
        .formatMessage({ id: `General.listing.type.boatEngines.${engine.engineType}` })
        .toLowerCase()
    );
  if (engine.engineYearBuilt) specs.push(`${engine.engineYearBuilt}`);
  if (engine.cylinders)
    specs.push(
      `${engine.cylinders} ${intl
        .formatMessage({ id: 'EditListingDetailsForm.cylinders' })
        ?.toLowerCase()}`
    );

  return specs.join(' • ');
};

const hasEngineDetails = engine => {
  return !!(
    engine.serialNumber ||
    engine.engineHours ||
    engine.capacity ||
    engine.strokes ||
    engine.cylinders ||
    engine.engineYearBuilt
  );
};

const formatEngineValue = (value, unit) => {
  if (value === undefined || value === null || value === '') return '';
  return unit ? `${value} ${unit}` : value.toString();
};

const SectionEngines = props => {
  const { engines } = props;

  const intl = useIntl();

  if (!engines || engines?.length < 1) return null;

  const numberOfEngines = engines.length;

  return (
    <div className={css.root}>
      <div className={css.header}>
        <H2 className={css.title} as="h2">
          <FormattedMessage id="ListingPage.enginesSubtitle" />
        </H2>
        <div className={css.boatsSummary}>
          {numberOfEngines} {`(${intl.formatMessage({ id: 'ListingPage.totalEngines' })})`}
        </div>
      </div>

      <div className={css.enginesContainer}>
        {engines?.map((engine, index) => {
          return (
            <div className={css.engine} key={index}>
              <H3 className={css.engineTitle}>{getEngineDisplayName(engine, index)}</H3>
              <div className={css.engineSpec}>
                {getEngineSpecs(engine, intl) && <div>{getEngineSpecs(engine, intl)}</div>}
              </div>
              {hasEngineDetails(engine) && (
                <div className={css.engineDetails}>
                  {engine.serialNumber && (
                    <div className={css.engineDetail}>
                      <div>
                        <FormattedMessage id="EditListingDetailsForm.serialNumber" />:
                      </div>
                      <span className={css.infoValue}>{engine.serialNumber}</span>
                    </div>
                  )}
                  {engine.engineHours && (
                    <div className={css.engineDetail}>
                      <div>
                        <FormattedMessage id="EditListingDetailsForm.engineHours" />:
                      </div>
                      <span className={css.infoValue}>
                        {formatEngineValue(engine.engineHours, 'hrs')}
                      </span>
                    </div>
                  )}
                  {engine.capacity && (
                    <div className={css.engineDetail}>
                      <div>
                        <FormattedMessage id="EditListingDetailsForm.capacity" />:
                      </div>
                      <span className={css.infoValue}>{formatEngineValue(engine.capacity)}</span>
                    </div>
                  )}
                  {engine.strokes && (
                    <div className={css.engineDetail}>
                      <div>
                        <FormattedMessage id="EditListingDetailsForm.strokes" />:
                      </div>
                      <span className={css.infoValue}>{formatEngineValue(engine.strokes)}</span>
                    </div>
                  )}
                  {engine.cylinders && (
                    <div className={css.engineDetail}>
                      <div>
                        <FormattedMessage id="EditListingDetailsForm.cylinders" />:
                      </div>
                      <span className={css.infoValue}>{formatEngineValue(engine.cylinders)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const SectionEnginesSimple = props => {
  const { engines } = props;
  const intl = useIntl();

  if (!engines || engines.length < 1) return null;

  return (
    <div className={css.root}>
      {engines.map((engine, index) => {
        const motorInfo = getEngineSpecs(engine, intl);
        return (
          <SectionTextMaybe
            key={index}
            text={motorInfo}
            heading="ListingPage.enginesNumber"
            headingValues={{ value: engines?.length > 1 ? ` (${index + 1})` : '' }}
          />
        );
      })}
    </div>
  );
};

export default SectionEngines;

SectionEngines.propTypes = {
  engines: arrayOf(propTypes.engine),
};

SectionEnginesSimple.propTypes = {
  engines: arrayOf(propTypes.engine),
};
